import React from 'react';
import PropTypes from 'prop-types';
import { Sparkles } from 'lucide-react';
import { useDarkMode } from '../contexts/DarkModeContext';
import { AnalyzeIcon, PeopleIcon, LightbulbIcon, TrophyIcon } from '../utils/icons/SuggestionIcons';

// Maps icon name strings (as received from an API) to icon components.
// Add new entries here as more icons are needed.
const ICON_MAP = {
  analyze: <AnalyzeIcon />,
  people: <PeopleIcon />,
  lightbulb: <LightbulbIcon />,
  trophy: <TrophyIcon />,
};

const DEFAULT_SUGGESTIONS = [
  // {
  //   id: 1,
  //   title: "Analyze Performance",
  //   description: "Show me performance metrics for my recent marketing campaigns",
  //   icon: "analyzed",
  // },
  // {
  //   id: 2,
  //   title: "Customer Insights",
  //   description: "What are the key trends in my customer demographics?",
  //   icon: "people",
  // },
  // {
  //   id: 3,
  //   title: "Content Ideas",
  //   description: "Generate content ideas for my social media channels",
  //   icon: "lightbulb",
  // },
  // {
  //   id: 4,
  //   title: "Competitor Analysis",
  //   description: "Compare our performance against top competitors",
  //   icon: "trophy",
  // },
];

// suggestions prop shape: Array<{ id, title, description, icon: string (keyof ICON_MAP) | ReactElement }>
// Pass an empty array to hide the cards entirely for a given customer.
const SuggestionCards = ({ onSelectPrompt, suggestions }) => {
  const { isDarkMode } = useDarkMode();

  const resolvedSuggestions = suggestions ?? DEFAULT_SUGGESTIONS;

  if (resolvedSuggestions.length === 0) return null;

  const resolveIcon = (icon) => {
    // Allow callers to pass a React element directly.
    if (React.isValidElement(icon)) {
      return icon;
    }
    if (typeof icon === 'string') {
      const resolved = ICON_MAP[icon];
      if (!resolved) {
        return <Sparkles size={20} />;
      }
      return resolved;
    }
    return <Sparkles size={20} />;
  };

  return (
    <div className="w-full">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {resolvedSuggestions.map(suggestion => (
          <button
            key={suggestion.id}
            onClick={() => onSelectPrompt(suggestion.description)}
            className={`
              flex items-start gap-4 p-4 rounded-xl transition-all duration-200 ease-in-out overflow-hidden
              ${isDarkMode 
                ? 'bg-slate-800/60 hover:bg-slate-700/70 shadow-[0_0_0_1px_rgba(51,65,85,0.5)] hover:shadow-[0_0_0_1px_rgba(71,85,105,0.7)]' 
                : 'bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300 shadow-sm'
              }
              text-left w-full cursor-pointer
            `}
          >
            <div className={`
              flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg
              ${isDarkMode ? 'bg-slate-700/80' : 'bg-slate-100'}
            `}>
              <span className={`${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
                {resolveIcon(suggestion.icon)}
              </span>
            </div>
            <div className="flex-1 min-w-0 overflow-hidden">
              <h4 className={`
                text-sm font-semibold mb-1
                ${isDarkMode ? 'text-white' : 'text-slate-900'}
              `}>
                {suggestion.title}
              </h4>
              <p className={`
                text-sm leading-relaxed whitespace-normal
                ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}
              `}>
                {suggestion.description}
              </p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

SuggestionCards.propTypes = {
  onSelectPrompt: PropTypes.func.isRequired,
  suggestions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      icon: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    })
  ),
};

export default SuggestionCards;